import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import className from 'classnames';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { ABUtils, Config, TrackingUtils, withPageContext } from '../../utils';
import { EntryPoint, LegType, LegTypeProps, TabTypeProp, TabTypePropDefault } from '../../models';

const AirportSelectorInputErrorMobile = ({ focus = false, errorMessage = '' }) => {
    return (
        <>
            {!!errorMessage && !focus && (
                <span className="odf-label odf-text-sm">
                    {errorMessage}
                </span>
            )}
        </>
    );
};

export const AirportSelectorInputMobile = ({
    departureOrArrival,
    displayedWord,
    focus,
    onChangeInput,
    onFocusInput,
    activeProduct,
    trackEvent,
    errorMessage,
}) => {
    const { t } = useTranslation();
    const { isInteractionImprovements, isCssVariationMobile } = ABUtils.getABs();
    const inputRef = useRef();
    const changeInput = (e) => {
        onChangeInput(e.target.value);
    };

    const onClickInput = (e) => {
        onFocusInput(e.target.value);
    };

    const handleTracking = (label) => {
        const action = TrackingUtils.ACTION[activeProduct.toUpperCase()];
        (activeProduct === EntryPoint.HOTELS || activeProduct === EntryPoint.DP) && trackEvent({ action, label });
    };

    const clearInput = () => {
        onChangeInput('');
        onFocusInput();
        inputRef.current.focus();
        handleTracking(TrackingUtils.LABEL.hotelsDestinationClean());
    };

    const getPlaceholderKey = (activeProduct) => {
        if (activeProduct === EntryPoint.HOTELS) {
            return 'home.hsaSearch.destination';
        }
        if (activeProduct === EntryPoint.TRAINS) {
            return 'airportSelector.trainStation.placeholder';
        }
        return 'airportSelector.placeholder';
    };

    const placeholderKey = getPlaceholderKey(activeProduct);

    const placeholder =
        isInteractionImprovements || isCssVariationMobile ? t(placeholderKey) : '';
    const showClearIcon = !isInteractionImprovements || displayedWord.length > 0;
    const inputClassName = className({
        'prisma-input': isCssVariationMobile,
        'odf-input odf-mobile-input': !isCssVariationMobile,
    });
    const labelClassName = className({
        'prisma-label prisma-label-valid': isCssVariationMobile,
        'odf-mobile-label odf-mobile-label-valid': !isCssVariationMobile,
    });
    const labelInnerClassName = className({
        'prisma-inner-label': isCssVariationMobile,
        'odf-mobile-inner-label': !isCssVariationMobile,
    });
    const iconsWrapperClassName = className({
        'prisma-form-icons': isCssVariationMobile,
        'odf-mobile-form-icons': !isCssVariationMobile,
    });
    const isRtl = Config.get('isRtl');
    const iconStyle = isRtl ? { left: '-16px' } : { right: '-16px' };
    const iconsWrapperStyle = isCssVariationMobile ? iconStyle : {};
    const iconValidClassName = className({
        'odf-space-outer-right-m prisma-input-icon prisma-input-icon-valid odf-icon-tick-circle': isCssVariationMobile,
        'odf-input-icon odf-mobile-input-icon-valid odf-icon-tick': !isCssVariationMobile,
        'odf-icon': true,
    });
    const iconClearClassName = className({
        'odf-space-outer-right-m prisma-input-icon prisma-input-icon-clear': isCssVariationMobile,
        'odf-input-icon odf-mobile-input-icon-clear': !isCssVariationMobile,
        'odf-icon odf-icon-cross-circle-light': true,
    });

    useEffect(() => {
        if (focus) {
            const fieldNameTracking = departureOrArrival === LegType.DEPARTURE ? 'dep': 'dest';
            inputRef.current.focus();
            inputRef.current.select();
            handleTracking(TrackingUtils.LABEL.fieldOpen(activeProduct, fieldNameTracking));
        } else {
            inputRef.current.blur();
        }
    }, [activeProduct, departureOrArrival, focus]);

    return (
        <>
            <div>
                <input
                    type="text"
                    className={inputClassName}
                    onChange={changeInput}
                    onClick={onClickInput}
                    ref={inputRef}
                    value={displayedWord}
                    placeholder={placeholder}
                    autoComplete="off"
                />
                <label className={labelClassName}>
                    {(isInteractionImprovements || isCssVariationMobile) && (
                        <span className={labelInnerClassName}>
                            {placeholder}
                        </span>
                    )}
                </label>
            </div>
            <div className={iconsWrapperClassName} style={iconsWrapperStyle}>
                <span className={iconValidClassName} />
                {showClearIcon && <span onClick={clearInput} className={iconClearClassName} />}
            </div>
            <AirportSelectorInputErrorMobile errorMessage={errorMessage} focus={focus} />
        </>
    );
};

AirportSelectorInputMobile.propTypes = {
    focus: PropTypes.bool.isRequired,
    displayedWord: PropTypes.string.isRequired,
    onChangeInput: PropTypes.func.isRequired,
    onFocusInput: PropTypes.func.isRequired,
    activeProduct: TabTypeProp,
    trackEvent: PropTypes.func.isRequired,
    errorMessage: PropTypes.string,
    departureOrArrival: LegTypeProps.isRequired,
};

AirportSelectorInputMobile.defaultProps = {
    activeProduct: TabTypePropDefault,
    errorMessage: '',
};

const mapStateToProps = ({ activeProduct }) => ({
    activeProduct: activeProduct.activeProduct,
});

const connected = connect(mapStateToProps, {})(withPageContext(AirportSelectorInputMobile));
export { connected as ConnectedAirportSelectorInputMobile };
